import {
  ButtonDS,
  DialogModalDS,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { triggerMemobankTransferAsync } from "../sepaDirectDebitSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectAdminUser } from "../../homeSlice";

type currentSepaToTrigger = {
  uuid: string;
  companyName: string;
  amount: number;
}

interface TriggerSepaDirectDebitModalProps {
  visible: boolean;
  onCloseModal: () => void;
  sepaDirectDebit: currentSepaToTrigger;
}

export const TriggerSepaDirectDebitModal = (props: TriggerSepaDirectDebitModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { visible, onCloseModal, sepaDirectDebit } = props;
  const adminUser = useAppSelector(selectAdminUser);

  const closeModal = useCallback(() => {
    onCloseModal();
  }, [onCloseModal]);

  const clickToSubmit = useCallback(() => {
    if (!adminUser) return;
    
    void dispatch(triggerMemobankTransferAsync({
      memobankSepaDirectDebitUuid: sepaDirectDebit.uuid,
      updatedBy: adminUser.email
    }));
    onCloseModal();
  }, [adminUser, dispatch, onCloseModal, sepaDirectDebit.uuid]);

  if (!visible || !sepaDirectDebit) {
    return;
  }

  return (
    <DialogModalDS
      visible={visible}
      onClose={closeModal}
      title={t("triggerSepaDirectDebit.modal.title")}
      width={40}
      bottomButton={
        <ButtonDS
          text={t(`triggerSepaDirectDebit.modal.confirm`)}
          format={"fill"}
          buttonType={"danger"}
          onClick={clickToSubmit}
        />
      }
    >
      <StyledColumn>
        <TextCapitalized>
          {t(`triggerSepaDirectDebit.modal.subtitle`, { companyName: sepaDirectDebit.companyName })}
        </TextCapitalized>
        <Spacer y={1} />
        <StyledAmount>
          <TextCapitalized>
            {t(`triggerSepaDirectDebit.modal.subtitle.amount`)}
          </TextCapitalized>
          <Spacer x={0.25} />
          <StyledAmountValue>
            {t(`triggerSepaDirectDebit.modal.subtitle.amount.value`, { amount: sepaDirectDebit.amount })}
          </StyledAmountValue>
        </StyledAmount>
        <Spacer y={2} />
        <StyledWarning>
          <TextCapitalized>
            {t(`triggerSepaDirectDebit.modal.subtitle.warning`)}
          </TextCapitalized>
        </StyledWarning>
        <Spacer y={2} />
        <TextCapitalized>
          {t(`triggerSepaDirectDebit.modal.irreversible`)}
        </TextCapitalized>
      </StyledColumn>
    </DialogModalDS>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const StyledWarning = styled.div`
  ${typographies["Header/H2"]}
  color: ${colors["colors/system/warning/warning_dark"]};
`;

const StyledAmount = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${typographies["Body/XL"]}
  color: ${colors["colors/text/black"]};
`;

const StyledAmountValue = styled.span`
  ${typographies["Body/XL"]}
  font-weight: bold;
  color: ${colors["colors/text/black"]};
`;
